/**
 * Auriga Frontend Application Entry Point
 *
 * This is the main JavaScript entry point for the Auriga Frontend application.
 * It initializes React and renders the root App component.
 */

// Shared library component stylesheets
import '@amzn/gft-dimension-picker/index.css';
import '@amzn/gft-fpa-one-layout/gft-fpa-one-layout.css';
// Import our GFT Base stylesheet with Amazon Ember fonts loaded
import '@amzn/gft-frontend-commons/GFTBaseStyles.css';
// Auriga dark mode CSS custom properties (imported once here — not in partials)
import './assets/stylesheets/dark-mode.scss';

import { ExcelExportModule, LicenseManager } from '@amzn/gft-ag-grid-enterprise';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { getAurigaSettings } from '@utils/cookies';
import { handleGlobalError } from '@utils/errorUtils';
import {
  AllCommunityModule,
  ClientSideRowModelModule,
  ModuleRegistry,
  RowDragModule,
  ValidationModule,
} from 'ag-grid-community';
import { isNil } from 'lodash';
import React from 'react';
import { createRoot } from 'react-dom/client';

import App from './App';

// Consolidated all Module Registry at entry point for AgGrid React to work at version 34+
// Add modules here as needed in any Components
ModuleRegistry.registerModules([
  AllCommunityModule,
  ClientSideRowModelModule,
  RowDragModule,
  ExcelExportModule, // Enterprise AG Grid Feature inclusion
  // Include ValidationModule for development
  // ValidationModule enabling allows AGGrid to add useful debugging output to the developer console for any warnings/errors
  // It is intentionally omitted for production to reduce the bundle size
  // See: https://www.ag-grid.com/javascript-data-grid/modules/#development-validations
  ...(process.env.NODE_ENV !== 'production' ? [ValidationModule] : []),
]);

// Parsed Auriga Settings to apply an AG Grid license key (if exists)
const aurigaSettings = getAurigaSettings();
// Register the AG Grid license key if available
if (!isNil(aurigaSettings.appParams?.agGridLicenseKey)) {
  LicenseManager.setLicenseKey(aurigaSettings.appParams.agGridLicenseKey);
}

// Create a new QueryClient instance with centralized error handling
const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: handleGlobalError,
  }),
  mutationCache: new MutationCache({
    onError: handleGlobalError,
  }),
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
});

// Initialize the React application
const container = document.getElementById('root');
if (container) {
  const root = createRoot(container);

  // Render the application with StrictMode for additional development checks
  root.render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </React.StrictMode>,
  );
}
